import Clarity from '@microsoft/clarity';
import mixpanel from 'mixpanel-browser';
import posthog from 'posthog-js';

/** Production GA4 measurement ID (shared web stream). */
export const GA_MEASUREMENT_ID_PRODUCTION = 'G-HXW25ZBCNY';

/** Staging GA4 measurement ID. */
export const GA_MEASUREMENT_ID_STAGING = 'G-SS4F5DR85N';

/** Production Clarity project ID  */
export const CLARITY_PROJECT_ID_PRODUCTION = 'xwlquge3so';

/** Staging Clarity project ID  */
export const CLARITY_PROJECT_ID_STAGING = 'xwm13qpcfm';

/** Production PostHog project API key  */
export const POSTHOG_KEY_PRODUCTION = 'phc_pRM3UpssWCjEGBhFGrzuMQPDxYzZqrumqNrK2YCfQGZD';

/** Staging PostHog project API key — replace PLACEHOLDER_STAGING_KEY with the real key. */
export const POSTHOG_KEY_STAGING = 'XXXXXXX';

/** PostHog API host — replace PLACEHOLDER_HOST with the real host (e.g. https://us.i.posthog.com). */
export const POSTHOG_HOST = 'https://us.i.posthog.com';

/** Production Mixpanel project token. */
export const MIXPANEL_TOKEN_PRODUCTION = '71ac4487688306dc78e09f40d8018b9b';

/** Staging Mixpanel project token — replace when a dedicated staging project exists. */
export const MIXPANEL_TOKEN_STAGING = '71ac4487688306dc78e09f40d8018b9b';

/** Meta (Facebook) Pixel ID — ad account "ShreyasTPWEBDONOTDELETE". Same ID across environments. */
export const META_PIXEL_ID = '1882899355634517';

/** Resolves the GA4 measurement ID for the current host. */
export const getGaMeasurementId = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('staging-')) {
    return GA_MEASUREMENT_ID_STAGING;
  }
  return GA_MEASUREMENT_ID_PRODUCTION;
};

/** Resolves the Clarity project ID for the current host. */
export const getClarityProjectId = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('staging-')) {
    return CLARITY_PROJECT_ID_STAGING;
  }
  return CLARITY_PROJECT_ID_PRODUCTION;
};

/** Resolves the PostHog project API key for the current host. */
export const getPostHogKey = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('staging-')) {
    return POSTHOG_KEY_STAGING;
  }
  return POSTHOG_KEY_PRODUCTION;
};

/** Resolves the Mixpanel project token for the current host. */
export const getMixpanelToken = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('staging-')) {
    return MIXPANEL_TOKEN_STAGING;
  }
  return MIXPANEL_TOKEN_PRODUCTION;
};

export const USER_TYPE_TENANT = 'tenants' as const;

export type AnalyticsUserType = typeof USER_TYPE_TENANT;

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

/** Key events for the tenant portal (see GA4 analytics brief §6). */
export const GaKeyEvent = {
  SignUpStart: 'sign_up_start',
  SignUp: 'sign_up',
  Login: 'login',
  OnboardingStart: 'onboarding_start',
  OnboardingStepCompleted: 'onboarding_step_completed',
  OnboardingCompleted: 'onboarding_completed',
  AutopayEnabled: 'autopay_enabled',
  AddPaymentInfo: 'add_payment_info',
  PaymentMethodAdded: 'payment_method_added',
  BeginCheckout: 'begin_checkout',
  PageView: 'page_view',
} as const;

/** Friendly Mixpanel / analytics page names for main tenant routes. */
export const AnalyticsPageName = {
  Login: 'Login',
  SignUp: 'Sign Up',
  Dashboard: 'Dashboard',
  PayRent: 'Pay Rent',
  Rewards: 'Rewards',
  RentDrop: 'Monthly Rent Drop',
  Wallet: 'Wallet',
  RentReporting: 'Rent Reporting',
  PaymentMethods: 'Payment Methods',
  Profile: 'Profile',
} as const;

export type AnalyticsPageNameValue = (typeof AnalyticsPageName)[keyof typeof AnalyticsPageName];

const PAGE_NAME_BY_PATH: ReadonlyArray<{ prefix: string; name: AnalyticsPageNameValue }> = [
  { prefix: '/login', name: AnalyticsPageName.Login },
  { prefix: '/sign-up', name: AnalyticsPageName.SignUp },
  { prefix: '/dashboard', name: AnalyticsPageName.Dashboard },
  { prefix: '/pay-rent', name: AnalyticsPageName.PayRent },
  { prefix: '/rewards', name: AnalyticsPageName.Rewards },
  { prefix: '/rent-drop', name: AnalyticsPageName.RentDrop },
  { prefix: '/wallet', name: AnalyticsPageName.Wallet },
  { prefix: '/rent-reporting', name: AnalyticsPageName.RentReporting },
  { prefix: '/payment-methods', name: AnalyticsPageName.PaymentMethods },
  { prefix: '/profile', name: AnalyticsPageName.Profile },
];

const ONBOARDING_COMPLETED_STORAGE_PREFIX = 'ga4_onboarding_completed_';
const ONBOARDING_STARTED_STORAGE_KEY = 'ga4_onboarding_started';
const ONBOARDING_STEP_COUNT_STORAGE_KEY = 'ga4_onboarding_step_count';

let mixpanelInitialized = false;

const canUseGtag = (): boolean =>
  typeof window !== 'undefined' && typeof window.gtag === 'function';

const canUseClarity = (): boolean =>
  typeof window !== 'undefined' && typeof window.clarity === 'function';

const canUsePostHog = (): boolean => typeof window !== 'undefined' && Boolean(posthog.__loaded);

const canUseMixpanel = (): boolean => typeof window !== 'undefined' && mixpanelInitialized;

const canUseFbq = (): boolean =>
  typeof window !== 'undefined' && typeof window.fbq === 'function';

const stripUndefinedParams = (params: AnalyticsParams): Record<string, string | number | boolean> =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  ) as Record<string, string | number | boolean>;

/**
 * Initialize Mixpanel once. Safe to call multiple times.
 */
export const initMixpanel = (): void => {
  if (typeof window === 'undefined' || mixpanelInitialized) return;

  mixpanel.init(getMixpanelToken(), {
    track_pageview: false,
    persistence: 'localStorage',
    ignore_dnt: false,
  });
  mixpanelInitialized = true;
  mixpanel.register({ user_type: USER_TYPE_TENANT });
};

/**
 * Map a pathname to a tracked page name, or undefined when the route is not a main page.
 */
export const getAnalyticsPageName = (pathname: string): AnalyticsPageNameValue | undefined => {
  const normalized = pathname.replace(/\/$/, '') || '/';

  if (normalized === '/' || normalized === '') {
    return AnalyticsPageName.Login;
  }

  const match = PAGE_NAME_BY_PATH.find(
    ({ prefix }) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
  return match?.name;
};

/**
 * Identify the logged-in user in Clarity.
 * Uses the opaque database user ID — never email or name.
 */
export const setClarityUser = (userId: string | undefined): void => {
  if (!canUseClarity() || !userId) return;

  Clarity.identify(userId);
};

/**
 * Identify the logged-in user in PostHog session replay.
 * Uses the opaque database user ID — never email or name.
 */
export const setPostHogUser = (userId: string | undefined): void => {
  if (!canUsePostHog() || !userId) return;

  posthog.identify(userId);
};

/**
 * Identify the logged-in user in Mixpanel.
 * Uses the opaque database user ID — never email or name.
 */
export const setMixpanelUser = (userId: string | undefined): void => {
  if (!canUseMixpanel() || !userId) return;

  mixpanel.identify(userId);
  mixpanel.people.set({ user_type: USER_TYPE_TENANT });
};

/**
 * Identify the logged-in user for cross-surface stitching (web ↔ app).
 * Uses the opaque database user ID — never email or name.
 * Clarity, PostHog, and Mixpanel are identified by tenantId when provided (else userId).
 */
export const setAnalyticsUser = (
  userId: string | number | undefined,
  userType: AnalyticsUserType = USER_TYPE_TENANT,
  tenantId?: string
): void => {
  if (!userId) return;

  const id = String(userId);
  // Session-replay tools keep their existing tenantId identity so returning users
  // retain history; only the GA4 user_id moves to the userId mapping.
  const toolId = tenantId ?? id;
  setClarityUser(toolId);
  setPostHogUser(toolId);
  setMixpanelUser(toolId);

  if (!canUseGtag()) return;

  // `user_id_{userId}` is the shared GA4 identity format across the tenant
  // portal, manager portal, and backend events, so the same person is stitched
  // into one GA4 user regardless of surface.
  window.gtag('set', { user_id: `user_id_${id}` });
  window.gtag('set', 'user_properties', { user_type: userType });
};

export const clearAnalyticsUser = (): void => {
  if (canUsePostHog()) {
    posthog.reset();
  }

  if (canUseMixpanel()) {
    mixpanel.reset();
    mixpanel.register({ user_type: USER_TYPE_TENANT });
  }

  sessionStorage.removeItem(ONBOARDING_STARTED_STORAGE_KEY);
  sessionStorage.removeItem(ONBOARDING_STEP_COUNT_STORAGE_KEY);

  if (!canUseGtag()) return;

  window.gtag('set', { user_id: undefined });
  window.gtag('set', 'user_properties', { user_type: undefined });
  // @microsoft/clarity has no clear-identity API; session ends on logout naturally.
};

/**
 * Fan-out event to GA4 and Mixpanel.
 */
export const trackEvent = (eventName: string, params: AnalyticsParams = {}): void => {
  const payload = {
    user_type: USER_TYPE_TENANT,
    ...params,
  };

  if (canUseGtag()) {
    window.gtag('event', eventName, payload);
  }

  if (canUseMixpanel()) {
    mixpanel.track(eventName, stripUndefinedParams(payload));
  }
};

export const trackSignUp = (method: string): void => {
  trackEvent(GaKeyEvent.SignUp, { method, user_type: USER_TYPE_TENANT });

  if (canUseFbq()) {
    window.fbq?.('track', 'CompleteRegistration', { content_name: method });
  }
};

/**
 * Fires when a user begins account creation (sign-up form or OAuth sign-up started).
 */
export const trackSignUpStart = (method: string): void => {
  trackEvent(GaKeyEvent.SignUpStart, { method, user_type: USER_TYPE_TENANT });
};

/**
 * Fires when a user successfully logs in (existing account).
 */
export const trackLogin = (method: string): void => {
  trackEvent(GaKeyEvent.Login, { method });
};

/**
 * Fires once per browser session when the onboarding flow's first step is shown.
 */
export const trackOnboardingStart = (): void => {
  if (sessionStorage.getItem(ONBOARDING_STARTED_STORAGE_KEY) === '1') return;

  sessionStorage.setItem(ONBOARDING_STARTED_STORAGE_KEY, '1');
  trackEvent(GaKeyEvent.OnboardingStart);
};

/**
 * Fires when an onboarding step is completed. step_number auto-increments per browser session.
 */
export const trackOnboardingStepCompleted = (stepName: string): void => {
  const nextStep = Number(sessionStorage.getItem(ONBOARDING_STEP_COUNT_STORAGE_KEY) || '0') + 1;
  sessionStorage.setItem(ONBOARDING_STEP_COUNT_STORAGE_KEY, String(nextStep));

  trackEvent(GaKeyEvent.OnboardingStepCompleted, { step_name: stepName, step_number: nextStep });
};

/**
 * Fires once per tenant when initial onboarding finishes (first property added).
 */
export const trackOnboardingCompletedOnce = (tenantId: string | undefined): void => {
  if (!tenantId) {
    trackEvent(GaKeyEvent.OnboardingCompleted);
    return;
  }

  const key = `${ONBOARDING_COMPLETED_STORAGE_PREFIX}${tenantId}`;
  if (localStorage.getItem(key) === '1') return;

  localStorage.setItem(key, '1');
  trackEvent(GaKeyEvent.OnboardingCompleted);
};

export const trackAutopayEnabled = (plan = 'autopay'): void => {
  trackEvent(GaKeyEvent.AutopayEnabled, { plan });
};

/**
 * Track successful addition of a payment method (card, bank/PAD, Plaid).
 * Fires both the internal `payment_method_added` event and GA4's standard
 * `add_payment_info` event — callers only need to call this once.
 */
export const trackPaymentMethodAdded = (
  method: 'card' | 'bank' | 'plaid',
  params: AnalyticsParams = {}
): void => {
  trackEvent(GaKeyEvent.PaymentMethodAdded, { method, ...params });
  trackEvent(GaKeyEvent.AddPaymentInfo, { payment_type: method, ...params });
};

/**
 * Fires when a payment flow starts (e.g. rent payment review step shown).
 */
export const trackBeginCheckout = (value: number, currency = 'CAD'): void => {
  trackEvent(GaKeyEvent.BeginCheckout, { value, currency });
};

/**
 * Track a named page view for main tenant portal surfaces.
 */
export const trackPageView = (
  pageName: AnalyticsPageNameValue | string,
  params: AnalyticsParams = {}
): void => {
  trackEvent(GaKeyEvent.PageView, { page_name: pageName, ...params });
};

const META_PIXEL_PATH_PREFIXES: readonly string[] = ['/login', '/sign-up'];

/**
 * The Meta Pixel is limited to the unauthenticated acquisition pages so that it never runs
 * alongside payment, KYB or profile screens.
 */
export const isMetaPixelPath = (pathname: string): boolean => {
  const normalized = pathname.replace(/\/$/, '') || '/';
  return (
    normalized === '/' ||
    META_PIXEL_PATH_PREFIXES.some(
      (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
    )
  );
};

/**
 * Session replay runs on the unauthenticated acquisition pages only, so payment, KYB and
 * profile screens are never recorded.
 */
export const syncSessionRecording = (pathname: string): void => {
  if (!canUsePostHog()) return;

  if (isMetaPixelPath(pathname)) {
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
  }
};

/**
 * Fires Meta Pixel's PageView on acquisition pages only. On any other route the pixel's
 * consent is revoked, so it stays inert once the user moves into the authenticated portal.
 */
export const trackMetaPixelPageView = (pathname: string): void => {
  if (!canUseFbq()) return;

  if (isMetaPixelPath(pathname)) {
    window.fbq?.('consent', 'grant');
    window.fbq?.('track', 'PageView');
  } else {
    window.fbq?.('consent', 'revoke');
  }
};
